import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import "../../../i18n";
import { lz } from "../../../i18n/localized";
import { Coverage, Notice } from "../../../design/components";
import { familyLabel, type Family } from "../../../domain/families";
import { openVersionOf, profileStateLine } from "./profileState";
import type { ProfileSummary } from "./types";
import styles from "./mapping.module.css";

/** One family's mapping on the company Mapping tab (HUB-F14) and, from HUB-F13, wizard step 4:
    the state line, the active version's coverage, the version links and one action. */
export function FamilyMappingCard({
  entityId,
  family,
  profile,
  busy = false,
  error = "",
  returnTo = "",
  createLabel,
  note,
  onCreate,
}: {
  entityId: string;
  family: Family;
  profile: ProfileSummary | undefined;
  busy?: boolean;
  error?: string;
  /** Appended as `?returnTo=` so the Studio can offer a way back, e.g. to the wizard. */
  returnTo?: string;
  /** The create button's text; the wizard names the pre-seed, the Mapping tab says `Create draft`. */
  createLabel?: string;
  /** One line under the heading, e.g. what the sales family covers. */
  note?: string | undefined;
  onCreate(family: Family): void;
}) {
  const { t } = useTranslation();
  const label = familyLabel(family);
  const createText = createLabel ?? t("mapping.createDraft", "Create draft");
  const query = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : "";
  const href = (version: number) => `/mapping/${entityId}/${family}/${version}${query}`;

  return (
    <section className="card" aria-label={label}>
      <div className="card-head">
        <h3 className="grow">{label}</h3>
      </div>
      <div className="card-body stack">
        {note ? <p className={styles.cardNote}>{note}</p> : null}
        <p>{profileStateLine(profile)}</p>
        {profile ? (
          <>
            <Coverage mappingCoverage={profile.coverage} />
            <ul>
              {profile.versions.map((item) => (
                <li key={item.id}>
                  <Link className="link" to={href(item.version)}>
                    {t("mapping.versionState", "Version {{version}} ({{state}})", {
                      version: item.version,
                      state: lz(`mapping.state.${item.state}`, item.state),
                    })}
                  </Link>
                </li>
              ))}
            </ul>
            <div>
              <Link className="btn btn-sm btn-primary" to={href(openVersionOf(profile))}>
                {t("mapping.openMapping", "Open mapping")}
              </Link>
            </div>
          </>
        ) : (
          <div>
            <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => onCreate(family)}>
              {createText}
            </button>
          </div>
        )}
        {error ? <Notice kind="fail">{error}</Notice> : null}
      </div>
    </section>
  );
}
